import { z } from 'zod';

import { makeTypescriptEnumSchema } from '../../enumSchemas';
import objectPassthrough from '../../objectPassthrough';
import { WithUnknownValuePlaceholder } from '../../unknownValuePlaceholder';
import { ProtectedValue, Unprotect, UnprotectedValue } from '../ProtectedValue';

export enum Fido2HmacCredentialAlgorithm {
  hmacSha256 = 'hmac-sha256',
}

// Reference: https://fidoalliance.org/specs/cx/cxf-v1.0-ps-20250814.html#dict-fido2-hmac-credentials
interface PasskeyHmacSecret {
  algorithm: WithUnknownValuePlaceholder<Fido2HmacCredentialAlgorithm, undefined>;
  credWithUV: ProtectedValue;
  credWithoutUV: ProtectedValue;
}
const PasskeyHmacSecret = objectPassthrough({
  algorithm: makeTypescriptEnumSchema(Fido2HmacCredentialAlgorithm, undefined),
  credWithUV: ProtectedValue,
  credWithoutUV: ProtectedValue,
});

type UnprotectedPasskeyHmacSecret = Unprotect<PasskeyHmacSecret, UnprotectedValue>;
const UnprotectedPasskeyHmacSecret = objectPassthrough({
  algorithm: makeTypescriptEnumSchema(Fido2HmacCredentialAlgorithm, undefined),
  credWithUV: UnprotectedValue,
  credWithoutUV: UnprotectedValue,
});

type UnprotectedStringPasskeyHmacSecret = Unprotect<PasskeyHmacSecret, string>;
const UnprotectedStringPasskeyHmacSecret = z.object({
  algorithm: makeTypescriptEnumSchema(Fido2HmacCredentialAlgorithm, undefined),
  credWithUV: z.string(),
  credWithoutUV: z.string(),
});

// Reference: https://fidoalliance.org/specs/cx/cxf-v1.0-ps-20250814.html#dict-passkey
interface Passkey {
  // NOTE: We omit the passkey type, as it is implicitly set by the way we store passkeys in a login.
  // The type field should be added back during export.
  // type: 'passkey';
  credentialId: string;
  rpId: string;
  username: string;
  userDisplayName: string;
  // Base64Url encoded
  userHandle: string;
  // Base64Url encoded
  key: ProtectedValue;
  hmacSecret?: PasskeyHmacSecret;
  // This is a cache of all related origins on which this passkey should also be
  // displayed besides the rpId and its registrable domain suffixes. We update
  // this cache whenever the passkey is used. Note that this key should be
  // stripped during export.
  relatedOrigins?: string[];
}
const Passkey = objectPassthrough({
  credentialId: z.string(),
  rpId: z.string(),
  username: z.string(),
  userDisplayName: z.string(),
  // Base64Url encoded
  userHandle: z.string(),
  // Base64Url encoded
  key: ProtectedValue,
  hmacSecret: PasskeyHmacSecret.optional(),
  relatedOrigins: z.array(z.string()).optional(),
});

// The unprotected version of the Passkey with an UnprotectedValue as its key.
// We need this for UnprotectedLogins.ts.
type UnprotectedPasskey = Unprotect<Passkey, UnprotectedValue>;
const UnprotectedPasskey = Passkey.omit({
  key: true,
  hmacSecret: true,
}).extend({
  key: UnprotectedValue,
  hmacSecret: UnprotectedPasskeyHmacSecret.optional(),
});

// Accessible version of the Passkey with a string as key
type UnprotectedStringPasskey = Unprotect<Passkey, string>;
const UnprotectedStringPasskey = Passkey.omit({
  key: true,
  hmacSecret: true,
}).extend({
  key: z.string(),
  hmacSecret: UnprotectedStringPasskeyHmacSecret.optional(),
});

export { Passkey, UnprotectedPasskey, UnprotectedStringPasskey, PasskeyHmacSecret };
